import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api.js';
import DeviceSetupGuide from './DeviceSetupGuide.jsx';
import ChargerQrCodes from './ChargerQrCodes.jsx';
import './charger-onboarding.scss';

/**
 * Charger Onboarding (NOC self-service): register a charger, enter the connection details on the unit,
 * watch it connect, run the live commissioning tests and switch it live for drivers.
 */
const LIFECYCLE = {
  provisioned: { label: 'Waiting for charger', cls: 'bg-secondary' },
  commissioning: { label: 'Commissioning', cls: 'bg-warning text-dark' },
  live: { label: 'Live', cls: 'bg-success' },
  retired: { label: 'Retired', cls: 'bg-dark' },
};
const TEST_BADGE = { Passed: 'bg-success', Failed: 'bg-danger', Skipped: 'bg-secondary', Running: 'bg-info text-dark', Pending: 'bg-light text-muted border' };
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

function Modal({ id, title, onClose, children, footer, size = 'lg' }) {
  return (
    <div className="modal d-block onb-modal-backdrop" id={id} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
      <div className={`modal-dialog modal-${size} modal-dialog-centered`}>
        <div className="modal-content shadow-lg bg-white">
          <div className="modal-header">
            <h5 className="modal-title fw-bold text-primary" id={`${id}-title`}>{title}</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body p-4">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function CopyField({ label, value, id }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the value is selectable */ }
  };
  return (
    <div className="mb-3">
      <div className="small fw-bold text-muted text-uppercase mb-1">{label}</div>
      <div className="d-flex gap-2 align-items-stretch">
        <div className="onb-secret flex-grow-1" id={id}>{value}</div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={copy} aria-label={`Copy ${label}`}>
          <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function ConnectionDetails({ charger, password, onOpenGuide }) {
  return (
    <>
      <CopyField label="Central system URL (OCPP server)" value={charger.connection.url} id="onb-conn-url" />
      <CopyField label="Charger ID (charge point identity)" value={charger.connection.chargeBoxId} id="onb-conn-id" />
      {charger.connection.authMode === 'basic' && password && (
        <>
          <CopyField label="Password (Basic auth / AuthorizationKey)" value={password} id="onb-conn-password" />
          <div className="alert alert-warning small py-2 mb-3" role="alert">
            <i className="fa fa-triangle-exclamation me-1" aria-hidden="true" />
            Copy the password now. It is shown only once; if it is lost, generate a new one.
          </div>
        </>
      )}
      {charger.connection.authMode === 'basic' && !password && (
        <p className="small text-muted">The password was shown when the charger was registered. Generate a new one if it is needed again.</p>
      )}
      {charger.connection.authMode === 'none' && <p className="small text-danger fw-semibold">This charger connects without a password. Use only on an isolated test network.</p>}
      <p className="small text-muted mb-0">
        Enter these in the charger&apos;s OCPP settings (on the unit or in the vendor&apos;s setup tool). Protocol: OCPP 1.6-J or 2.0.1 over WebSocket.
        Some chargers ask for the URL without the charger ID at the end and add it themselves.
      </p>
      <button type="button" className="btn btn-link btn-sm p-0 mt-2" id="link-device-guide" onClick={onOpenGuide}>Step-by-step instructions for the device</button>
    </>
  );
}

function AddChargerWizard({ options, onClose, onCreated, onSiteCreated }) {
  const [step, setStep] = useState(1);
  const [siteMode, setSiteMode] = useState(options.sites.length ? 'existing' : 'new');
  const [siteId, setSiteId] = useState(options.sites[0]?.id || '');
  const [site, setSite] = useState({ siteCode: '', siteName: '', address: '', city: '', latitude: '', longitude: '', operatingHours: '24/7' });
  const [profileId, setProfileId] = useState(options.profiles[0]?.id || '');
  const [serialNumber, setSerialNumber] = useState('');
  const [chargeBoxId, setChargeBoxId] = useState('');
  const [authMode, setAuthMode] = useState('basic');
  const [connectors, setConnectors] = useState([{ type: 'CCS2', maxPowerKw: 60 }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const profile = options.profiles.find((p) => p.id === profileId);

  const next = async () => {
    setError('');
    if (step === 1 && siteMode === 'new') {
      setBusy(true);
      try {
        const created = await api.request('/onboarding/sites', { method: 'POST', body: { ...site, operatingHours: site.operatingHours || undefined } });
        onSiteCreated(created);
        setSiteId(created.id);
        setSiteMode('existing');
      } catch (e) {
        setBusy(false);
        setError(e.details?.map?.((d) => d.message).join(' · ') || e.message);
        return;
      }
      setBusy(false);
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const created = await api.request('/onboarding/chargers', {
        method: 'POST',
        body: { siteId, profileId, authMode, connectors: connectors.map((c) => ({ type: c.type, maxPowerKw: Number(c.maxPowerKw) })), ...(serialNumber ? { serialNumber } : {}), ...(chargeBoxId ? { chargeBoxId } : {}) },
      });
      onCreated(created);
    } catch (e) {
      setError(e.details?.map?.((d) => d.message).join(' · ') || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setConn = (i, patch) => setConnectors((list) => list.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const siteField = (key, label, props = {}) => (
    <div className={props.col || 'col-12 col-md-6'}>
      <label className="form-label small fw-bold" htmlFor={`onb-site-${key}`}>{label}</label>
      <input className="form-control" id={`onb-site-${key}`} value={site[key]} onChange={(e) => setSite({ ...site, [key]: e.target.value })} placeholder={props.placeholder} inputMode={props.inputMode} />
    </div>
  );
  const step1Valid = siteMode === 'existing' ? !!siteId : site.siteCode && site.siteName && site.address && site.city && site.latitude !== '' && site.longitude !== '';
  const step3Valid = connectors.every((c) => Number(c.maxPowerKw) > 0);

  return (
    <Modal
      id="modal-add-charger"
      title={`Add a charger — step ${step} of 3`}
      onClose={onClose}
      footer={(
        <>
          {step > 1 && <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>}
          {step < 3 && <button type="button" className="btn btn-primary" id="btn-wizard-next" onClick={next} disabled={busy || (step === 1 ? !step1Valid : !profileId)}>Next</button>}
          {step === 3 && <button type="button" className="btn btn-primary" id="btn-wizard-create" onClick={submit} disabled={busy || !step3Valid}>{busy ? 'Registering…' : 'Register charger'}</button>}
        </>
      )}
    >
      {error && <div className="alert alert-danger py-2 small" role="alert" id="onb-wizard-error">{error}</div>}

      {step === 1 && (
        <>
          <h6 className="fw-bold mb-3">Where is the charger installed?</h6>
          <div className="btn-group mb-3" role="group" aria-label="Site choice">
            <button type="button" className={`btn btn-sm btn-outline-primary${siteMode === 'existing' ? ' active' : ''}`} onClick={() => setSiteMode('existing')} disabled={!options.sites.length}>Existing site</button>
            <button type="button" className={`btn btn-sm btn-outline-primary${siteMode === 'new' ? ' active' : ''}`} id="btn-site-new" onClick={() => setSiteMode('new')}>New site</button>
          </div>
          {siteMode === 'existing' ? (
            <select className="form-select" id="onb-site-select" aria-label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              {options.sites.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.city} ({s.code})</option>)}
            </select>
          ) : (
            <div className="row g-3">
              {siteField('siteCode', 'Site code', { placeholder: 'CEB-IT-01' })}
              {siteField('siteName', 'Site name', { placeholder: 'Cebu IT Park Plaza' })}
              {siteField('address', 'Address', { col: 'col-12' })}
              {siteField('city', 'City')}
              {siteField('operatingHours', 'Operating hours', { placeholder: '24/7' })}
              {siteField('latitude', 'Latitude', { placeholder: '10.3157', inputMode: 'decimal' })}
              {siteField('longitude', 'Longitude', { placeholder: '123.8854', inputMode: 'decimal' })}
              <div className="col-12 small text-muted">A new site stays hidden from drivers until its first charger goes live.</div>
            </div>
          )}
        </>
      )}

      {step === 2 && (
        <>
          <h6 className="fw-bold mb-3">Which charger is it?</h6>
          <div className="mb-3">
            <label className="form-label small fw-bold" htmlFor="onb-profile">Charger model</label>
            <select className="form-select" id="onb-profile" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              {options.profiles.map((p) => <option key={p.id} value={p.id}>{p.vendor} — {p.model} (OCPP {p.ocppVersion}, {p.tier})</option>)}
            </select>
            {profile?.notes && <div className="form-text">{profile.notes}</div>}
            <div className="form-text">Model missing? An administrator can add it under Charger Models &amp; Compatibility.</div>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold" htmlFor="onb-serial">Serial number (optional)</label>
              <input className="form-control" id="onb-serial" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold" htmlFor="onb-cbid">Charger ID (optional)</label>
              <input className="form-control" id="onb-cbid" value={chargeBoxId} onChange={(e) => setChargeBoxId(e.target.value)} placeholder="Generated from the site code" />
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold" htmlFor="onb-auth">Connection security</label>
              <select className="form-select" id="onb-auth" value={authMode} onChange={(e) => setAuthMode(e.target.value)}>
                <option value="basic">Password (HTTP Basic over WebSocket) — recommended</option>
                <option value="none">No password — isolated test network only</option>
              </select>
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <h6 className="fw-bold mb-3">Connectors on this charger</h6>
          {connectors.map((c, i) => (
            <div className="row g-2 align-items-end mb-2" key={`conn-${i + 1}`}>
              <div className="col-2 col-md-1 fw-bold pb-2">#{i + 1}</div>
              <div className="col-5 col-md-5">
                <label className="form-label small fw-bold" htmlFor={`onb-conn-type-${i}`}>Plug type</label>
                <select className="form-select" id={`onb-conn-type-${i}`} value={c.type} onChange={(e) => setConn(i, { type: e.target.value })}>
                  {options.connectorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-3 col-md-4">
                <label className="form-label small fw-bold" htmlFor={`onb-conn-kw-${i}`}>Max kW</label>
                <input className="form-control" id={`onb-conn-kw-${i}`} inputMode="decimal" value={c.maxPowerKw} onChange={(e) => setConn(i, { maxPowerKw: e.target.value })} />
              </div>
              <div className="col-2 col-md-2">
                <button type="button" className="btn btn-outline-danger w-100" aria-label={`Remove connector ${i + 1}`} disabled={connectors.length === 1} onClick={() => setConnectors(connectors.filter((_, idx) => idx !== i))}>
                  <i className="fa fa-trash" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-primary mt-2" id="btn-add-connector" disabled={connectors.length >= 8} onClick={() => setConnectors([...connectors, { type: 'CCS2', maxPowerKw: 60 }])}>
            <i className="fa fa-plus me-1" aria-hidden="true" />Add connector
          </button>
          <p className="small text-muted mt-3 mb-0">Connector numbers must match the charger&apos;s own numbering (connector 1, 2, …).</p>
        </>
      )}
    </Modal>
  );
}

function SettingsModal({ settings, onClose, onSaved }) {
  const [form, setForm] = useState({ ...settings });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const saved = await api.request('/onboarding/settings', {
        method: 'PUT',
        body: {
          publicWsUrl: form.publicWsUrl.trim(), heartbeatIntervalSeconds: Number(form.heartbeatIntervalSeconds), meterValueIntervalSeconds: Number(form.meterValueIntervalSeconds),
          offlineAfterSeconds: Number(form.offlineAfterSeconds), startTimeoutSeconds: Number(form.startTimeoutSeconds), requireTestsBeforeGoLive: !!form.requireTestsBeforeGoLive,
        },
      });
      onSaved(saved);
    } catch (e) {
      setError(e.details?.map?.((d) => d.message).join(' · ') || e.message);
    } finally {
      setBusy(false);
    }
  };
  const numberField = (key, label, help) => (
    <div className="col-12 col-md-4">
      <label className="form-label small fw-bold" htmlFor={`onb-set-${key}`}>{label}</label>
      <input className="form-control" id={`onb-set-${key}`} inputMode="numeric" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      <div className="form-text">{help}</div>
    </div>
  );
  return (
    <Modal id="modal-onb-settings" title="OCPP network settings" onClose={onClose} footer={<button type="button" className="btn btn-primary" id="btn-save-settings" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>}>
      {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
      <div className="mb-3">
        <label className="form-label small fw-bold" htmlFor="onb-set-url">Public OCPP server URL</label>
        <input className="form-control" id="onb-set-url" value={form.publicWsUrl} onChange={(e) => setForm({ ...form, publicWsUrl: e.target.value })} placeholder="wss://chargers.example.com/ocpp" />
        <div className="form-text">The address chargers reach ChargeOS on. Leave empty to use this site&apos;s own address (currently {settings.effectiveWsUrl}).</div>
      </div>
      <div className="row g-3">
        {numberField('heartbeatIntervalSeconds', 'Heartbeat (s)', 'How often chargers check in')}
        {numberField('meterValueIntervalSeconds', 'Meter values (s)', 'Reading interval while charging')}
        {numberField('offlineAfterSeconds', 'Offline after (s)', 'Silence before a charger counts as offline')}
        {numberField('startTimeoutSeconds', 'Plug-in timeout (s)', 'How long a started session waits for the vehicle before it is cancelled')}
      </div>
      <div className="form-check form-switch mt-3">
        <input className="form-check-input" type="checkbox" id="onb-set-require" checked={!!form.requireTestsBeforeGoLive} onChange={(e) => setForm({ ...form, requireTestsBeforeGoLive: e.target.checked })} />
        <label className="form-check-label" htmlFor="onb-set-require">Require passing commissioning tests before a charger can go live</label>
      </div>
      <p className="small text-muted mt-3 mb-0">Intervals are sent to every charger each time it boots.</p>
    </Modal>
  );
}

export default function ChargerOnboardingPage() {
  const [data, setData] = useState({ chargers: [], summary: {} });
  const [options, setOptions] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [password, setPassword] = useState(null); // { chargerId, value } — shown once after register / rotate
  const [modal, setModal] = useState(null); // 'add' | 'settings' | 'guide'
  const [includeReset, setIncludeReset] = useState(false);
  const [openTest, setOpenTest] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const toastTimer = useRef(null);

  const notify = (ok, text) => {
    setToast({ ok, text });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const loadList = useCallback(async () => {
    try {
      setData(await api.request('/onboarding/chargers'));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const [d, m] = await Promise.all([api.request(`/onboarding/chargers/${id}`), api.request(`/onboarding/chargers/${id}/messages?limit=60`)]);
      setDetail(d);
      setMessages(m);
    } catch (e) {
      if (e.status === 404) { setSelectedId(null); setDetail(null); }
    }
  }, []);

  useEffect(() => {
    loadList();
    api.request('/onboarding/options').then(setOptions).catch(() => {});
    api.request('/onboarding/settings').then(setSettings).catch(() => {});
  }, [loadList]);

  // Live view: the list and the open charger refresh every few seconds so connection and test progress show up by themselves.
  useEffect(() => {
    const t = setInterval(() => { loadList(); loadDetail(selectedId); }, 3000);
    return () => clearInterval(t);
  }, [loadList, loadDetail, selectedId]);

  const select = (id) => {
    setSelectedId(id);
    setDetail(null);
    setMessages([]);
    setOpenTest(null);
    loadDetail(id);
  };

  const act = async (key, fn, okText) => {
    setBusy(key);
    try {
      const result = await fn();
      if (okText) notify(true, okText);
      await Promise.all([loadList(), loadDetail(selectedId)]);
      return result;
    } catch (e) {
      notify(false, e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const onCreated = (created) => {
    setModal(null);
    setPassword(created.credentials ? { chargerId: created.id, value: created.credentials.password } : null);
    setSelectedId(created.id);
    setDetail(created);
    setMessages([]);
    loadList();
    notify(true, `${created.chargeBoxId} registered. Enter the connection details on the charger.`);
  };

  const rotate = () => act('rotate', async () => {
    const r = await api.request(`/onboarding/chargers/${selectedId}/credentials`, { method: 'POST' });
    setPassword({ chargerId: r.id, value: r.credentials.password });
  }, 'New password generated. The charger must be updated with it.');

  const remove = async () => {
    const r = await act('remove', () => api.request(`/onboarding/chargers/${selectedId}`, { method: 'DELETE' }));
    if (r) { notify(true, r.retired ? 'Charger retired (it has session history).' : 'Charger removed.'); setSelectedId(null); setDetail(null); }
  };

  const run = detail?.latestRun;
  const running = run?.status === 'running';
  const shownPassword = password && detail && password.chargerId === detail.id ? password.value : null;
  const tile = (label, value, id) => (
    <div className="col-6 col-lg-3">
      <div className="card p-3 shadow-sm border h-100">
        <div className="small text-muted text-uppercase fw-bold">{label}</div>
        <div className="h3 fw-bold mb-0 text-primary" id={id}>{value ?? 0}</div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4" id="charger-onboarding-page">
      <div className="row mb-3 align-items-center">
        <div className="col-12 col-lg-7 mb-2 mb-lg-0">
          <h1 className="h3 fw-bold mb-1 text-primary"><i className="fa fa-plug-circle-plus me-2" aria-hidden="true" />Charger Onboarding</h1>
          <p className="text-muted small mb-0">Register a charger, connect it over OCPP, run the live commissioning tests and switch it live for drivers.</p>
        </div>
        <div className="col-12 col-lg-5 d-flex flex-wrap justify-content-lg-end gap-2">
          <button type="button" className="btn btn-outline-secondary" id="btn-device-guide" onClick={() => setModal('guide')}><i className="fa fa-book-open me-2" aria-hidden="true" />Device setup guide</button>
          <button type="button" className="btn btn-outline-secondary" id="btn-onb-settings" onClick={() => setModal('settings')} disabled={!settings}><i className="fa fa-sliders me-2" aria-hidden="true" />Network settings</button>
          <button type="button" className="btn btn-primary" id="btn-add-charger" onClick={() => setModal('add')} disabled={!options}><i className="fa fa-plus me-2" aria-hidden="true" />Add charger</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="row g-3 mb-4">
        {tile('Waiting for charger', data.summary.provisioned, 'onb-count-provisioned')}
        {tile('Commissioning', data.summary.commissioning, 'onb-count-commissioning')}
        {tile('Live', data.summary.live, 'onb-count-live')}
        {tile('Connected now', data.summary.connected, 'onb-count-connected')}
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm border">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" id="onb-charger-table">
                  <thead>
                    <tr className="text-muted small text-uppercase"><th>Charger</th><th>Site</th><th>Model</th><th>Link</th><th>Stage</th></tr>
                  </thead>
                  <tbody>
                    {data.chargers.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">No chargers yet. Use &quot;Add charger&quot; to register the first one.</td></tr>}
                    {data.chargers.map((c) => (
                      <tr key={c.id} className={`onb-row${c.id === selectedId ? ' active' : ''}`} data-charge-box-id={c.chargeBoxId} tabIndex={0} onClick={() => select(c.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(c.id); } }}>
                        <td className="fw-bold">{c.chargeBoxId}</td>
                        <td className="small">{c.site?.name || '—'}</td>
                        <td className="small">{c.vendor} {c.model}</td>
                        <td><span className={`onb-dot${c.connected ? ' on' : ''}`} title={c.connected ? `Connected (OCPP ${c.ocppVersion})` : 'Not connected'} /><span className="visually-hidden">{c.connected ? 'Connected' : 'Not connected'}</span></td>
                        <td><span className={`badge ${LIFECYCLE[c.lifecycle]?.cls}`}>{LIFECYCLE[c.lifecycle]?.label}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6" id="onb-detail">
          {!selectedId && <div className="card p-4 shadow-sm border text-muted">Select a charger to see its connection details, tests and message log.</div>}
          {selectedId && !detail && <div className="card p-4 shadow-sm border text-muted">Loading…</div>}
          {detail && (
            <>
              <div className="card p-3 shadow-sm border mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                  <div>
                    <h2 className="h5 fw-bold mb-1 text-primary" id="onb-detail-title">{detail.chargeBoxId}</h2>
                    <div className="small text-muted">{detail.vendor} {detail.model} · {detail.site?.name}</div>
                  </div>
                  <div className="text-end">
                    <span className={`badge ${LIFECYCLE[detail.lifecycle]?.cls}`} id="onb-detail-lifecycle">{LIFECYCLE[detail.lifecycle]?.label}</span>
                    <div className="small mt-1" id="onb-detail-connection"><span className={`onb-dot${detail.connected ? ' on' : ''} me-1`} />{detail.connected ? `Connected · OCPP ${detail.ocppVersion}` : 'Not connected'}</div>
                  </div>
                </div>
                <div id="onb-checklist">
                  {detail.checklist.map((s) => (
                    <div key={s.key} className={`onb-step${s.done ? ' done' : ''}`} data-step={s.key} data-done={s.done}>
                      <i className={`fa ${s.done ? 'fa-circle-check' : 'fa-circle'}`} aria-hidden="true" />{s.label}
                    </div>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {detail.lifecycle !== 'live' && detail.lifecycle !== 'retired' && (
                    <button type="button" className="btn btn-success" id="btn-go-live" disabled={!detail.canGoLive || !!busy} onClick={() => act('live', () => api.request(`/onboarding/chargers/${selectedId}/go-live`, { method: 'POST', body: {} }), `${detail.chargeBoxId} is now live for drivers.`)}>
                      <i className="fa fa-bolt me-2" aria-hidden="true" />Go live
                    </button>
                  )}
                  {detail.lifecycle === 'live' && (
                    <button type="button" className="btn btn-outline-warning" id="btn-take-offline" disabled={!!busy} onClick={() => act('offline', () => api.request(`/onboarding/chargers/${selectedId}/take-offline`, { method: 'POST' }), 'Charger taken out of service.')}>
                      <i className="fa fa-power-off me-2" aria-hidden="true" />Take out of service
                    </button>
                  )}
                  {detail.lifecycle !== 'live' && (
                    <button type="button" className="btn btn-outline-danger" id="btn-remove-charger" disabled={!!busy} onClick={remove}><i className="fa fa-trash me-2" aria-hidden="true" />Remove</button>
                  )}
                </div>
                {detail.goLiveBlockedBy && detail.lifecycle !== 'live' && (
                  <div className="alert alert-warning small py-2 mt-2 mb-0" role="alert" id="onb-golive-blocked"><i className="fa fa-triangle-exclamation me-1" aria-hidden="true" />{detail.goLiveBlockedBy}</div>
                )}
                {detail.latestRun?.status === 'passed' && !detail.chargingVerified && (
                  <div className="small text-warning-emphasis mt-2" id="onb-charging-unverified">
                    <i className="fa fa-circle-info me-1" aria-hidden="true" />Charging itself has not been verified yet: the last run had no vehicle plugged in, so starting, metering and stopping a real charge were skipped. Run the tests once more with a vehicle or test load connected.
                  </div>
                )}
                {detail.lifecycle !== 'live' && !detail.canGoLive && (
                  <div className="small text-muted mt-2">{detail.connected ? 'Run the commissioning tests; the charger can go live once they pass.' : 'Waiting for the charger to connect with the details below.'}</div>
                )}
              </div>

              <div className="card p-3 shadow-sm border mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h3 className="h6 fw-bold mb-0"><i className="fa fa-link me-2" aria-hidden="true" />Connection details</h3>
                  <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-rotate-password" disabled={!!busy} onClick={rotate}>New password</button>
                </div>
                <ConnectionDetails charger={detail} password={shownPassword} onOpenGuide={() => setModal('guide')} />
              </div>

              <ChargerQrCodes chargeBoxId={detail.chargeBoxId} siteName={detail.site?.name} connectors={detail.connectors} />

              <div className="card p-3 shadow-sm border mb-3">
                <h3 className="h6 fw-bold mb-2"><i className="fa fa-microchip me-2" aria-hidden="true" />Reported by the charger</h3>
                {detail.lastBootAt ? (
                  <div className="small" id="onb-reported">
                    <div><strong>{detail.reported.vendor} {detail.reported.model}</strong> · firmware {detail.reported.firmware || '—'} · serial {detail.reported.serial || '—'}</div>
                    <div className="text-muted">Last boot {when(detail.lastBootAt)} · last seen {when(detail.lastSeenAt)} · IP {detail.ipAddress || '—'}</div>
                    {detail.firmwareDrift && <div className="text-danger fw-semibold">Firmware does not match the pattern in the model profile.</div>}
                  </div>
                ) : <div className="small text-muted">Nothing yet — the charger has not sent a BootNotification.</div>}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {detail.connectors.map((c) => <span key={c.id} className="badge bg-light text-dark border">#{c.index} {c.type} {c.maxPowerKw} kW · {c.status}</span>)}
                </div>
              </div>

              <div className="card p-3 shadow-sm border mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <h3 className="h6 fw-bold mb-0"><i className="fa fa-vial me-2" aria-hidden="true" />Live commissioning tests</h3>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check mb-0">
                      <input className="form-check-input" type="checkbox" id="onb-include-reset" checked={includeReset} onChange={(e) => setIncludeReset(e.target.checked)} />
                      <label className="form-check-label small" htmlFor="onb-include-reset">Include soft reset</label>
                    </div>
                    <button type="button" className="btn btn-primary" id="btn-run-tests" disabled={!detail.connected || running || !!busy} onClick={() => act('tests', () => api.request(`/onboarding/chargers/${selectedId}/tests`, { method: 'POST', body: { includeReset } }), 'Tests started.')}>
                      {running ? 'Running…' : 'Run tests'}
                    </button>
                  </div>
                </div>
                {!run && <div className="small text-muted">No test run yet. The tests talk to the connected charger and take about a minute. Plug in a vehicle or test load to also verify a real charging transaction.</div>}
                {run && (
                  <>
                    <div className="small mb-2" id="onb-run-summary" data-status={run.status}>
                      <span className={`badge ${run.status === 'passed' ? 'bg-success' : run.status === 'failed' ? 'bg-danger' : 'bg-info text-dark'} me-2`}>{run.status}</span>
                      {run.passed} passed · {run.failed} failed · {run.skipped} skipped · started {when(run.startedAt)}{run.initiatedBy ? ` by ${run.initiatedBy}` : ''}
                    </div>
                    {run.steps.map((s) => (
                      <div className="onb-test" key={s.id} data-test-id={s.id} data-status={s.status}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-semibold small">{s.id} · {s.name}</div>
                            <div className="small text-muted">{s.detail || s.expected}</div>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            {(s.request || s.response) && <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setOpenTest(openTest === s.id ? null : s.id)}>{openTest === s.id ? 'Hide' : 'Details'}</button>}
                            <span className={`badge ${TEST_BADGE[s.status]}`}>{s.status}</span>
                          </div>
                        </div>
                        {openTest === s.id && <pre>{JSON.stringify({ sent: s.request, received: s.response, latencyMs: s.latencyMs }, null, 2)}</pre>}
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="card p-3 shadow-sm border">
                <h3 className="h6 fw-bold mb-2"><i className="fa fa-terminal me-2" aria-hidden="true" />OCPP message log <span className="text-muted fw-normal small">(latest first, refreshes automatically)</span></h3>
                <div className="onb-log" id="onb-message-log">
                  {messages.length === 0 && <div className="text-muted">No messages yet.</div>}
                  {messages.map((m) => (
                    <div key={m.id} className={m.kind === 'error' || m.action === 'Rejected' ? 'err' : m.direction}>
                      {new Date(m.at).toLocaleTimeString()} {m.direction === 'in' ? '◀ charger' : '▶ ChargeOS'} {m.kind === 'connection' ? '' : `${m.kind} `}{m.action || ''} {m.payload ? JSON.stringify(m.payload).slice(0, 220) : ''}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {modal === 'add' && options && <AddChargerWizard options={options} onClose={() => setModal(null)} onCreated={onCreated} onSiteCreated={(s) => setOptions((o) => ({ ...o, sites: [...o.sites, s] }))} />}
      {modal === 'guide' && (
        <Modal id="modal-device-guide" title="Connecting a charger: what to do on the device" size="xl" onClose={() => setModal(null)}>
          <DeviceSetupGuide charger={detail} />
        </Modal>
      )}
      {modal === 'settings' && settings && <SettingsModal settings={settings} onClose={() => setModal(null)} onSaved={(s) => { setSettings(s); setModal(null); notify(true, 'Settings saved.'); loadDetail(selectedId); }} />}
      {toast && <div className={`onb-toast alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="onb-toast">{toast.text}</div>}
    </div>
  );
}
